import React, { useState, useEffect, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  increment,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';

// ==== Firebase config (already filled in) ====
const firebaseConfig = {
  apiKey: 'AIzaSyBqwFktbIIGw_otXkS_TiOTGjcJTZobt_g',
  authDomain: 'pritchardwedding-bridalparty.firebaseapp.com',
  projectId: 'pritchardwedding-bridalparty',
  storageBucket: 'pritchardwedding-bridalparty.firebasestorage.app',
  messagingSenderId: '1010413630292',
  appId: '1:1010413630292:web:2b013f66e8f2b2cb634ec1',
};

// PARTY_PASSWORD: shared with the whole bridal party.
// ADMIN_PASSWORD: yours/Zoe's only - gates the Full Details tab,
// including the Events, Polls, and Settings admin panels.
const PARTY_PASSWORD = 'letsparty!';
const ADMIN_PASSWORD = 'bubbazukoman';

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const EVENT_LIST = [
  'Bachelorette Party - July 2027',
  'Bridal Shower - September 2027',
  'Bridal Brunch - October 2027',
  'Wedding Rehearsal and Dinner - November 2027',
  'Wedding - November 2027',
];

const SUGGESTION_CATEGORIES = [
  { id: 'games', label: 'Games', icon: 'game' },
  { id: 'airbnb', label: 'AirBnb Ideas', icon: 'home' },
  { id: 'activities', label: 'Things To Do', icon: 'sparkle' },
  { id: 'other', label: 'Other', icon: 'lightbulb' },
];

// ---------- Small shared utilities ----------
function formatPhoneNumber(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
  const len = digits.length;
  if (len === 0) return '';
  if (len < 4) return `(${digits}`;
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function getStoredName() {
  return localStorage.getItem('bp-display-name') || '';
}
function setStoredName(name) {
  localStorage.setItem('bp-display-name', name);
}

function formatEventDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}
function formatEventTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const dt = new Date();
  dt.setHours(h, m, 0, 0);
  return dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function buildICS(ev) {
  const [y, m, d] = ev.date.split('-').map(Number);
  const [sh, sm] = (ev.startTime || '09:00').split(':').map(Number);
  const start = new Date(y, m - 1, d, sh, sm);
  let end;
  if (ev.endTime) {
    const [eh, em] = ev.endTime.split(':').map(Number);
    end = new Date(y, m - 1, d, eh, em);
  } else {
    end = new Date(start.getTime() + 60 * 60 * 1000);
  }
  const toICS = (dt) => dt.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const esc = (s = '') => String(s).replace(/[,;]/g, (c) => `\\${c}`).replace(/\n/g, '\\n');
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Bridal Party Dashboard//EN',
    'BEGIN:VEVENT',
    `UID:${ev.id || Date.now()}@bridalparty.pritchardwedding`,
    `DTSTAMP:${toICS(new Date())}`,
    `DTSTART:${toICS(start)}`,
    `DTEND:${toICS(end)}`,
    `SUMMARY:${esc(ev.title)}`,
    ev.location ? `LOCATION:${esc(ev.location)}` : '',
    ev.notes ? `DESCRIPTION:${esc(ev.notes)}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean).join('\r\n');
}

function downloadICS(ev) {
  const ics = buildICS(ev);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(ev.title || 'event').replace(/[^a-z0-9]/gi, '-')}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------- Icons ----------
const Icon = ({ name, size = 18 }) => {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'overview':
      return <svg {...common}><path d="M4 4v16M4 4h16M4 12h16M4 20h16" /></svg>;
    case 'members':
      return <svg {...common}><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M16 4.5c1.7.4 3 2 3 3.9 0 1.9-1.3 3.5-3 3.9M21.5 20c0-2.8-2-5.1-4.7-5.8" /></svg>;
    case 'sizes':
      return <svg {...common}><path d="M4 8l8-4 8 4-8 4-8-4z" /><path d="M4 8v8l8 4 8-4V8" /><path d="M12 12v8" /></svg>;
    case 'playlist':
      return <svg {...common}><circle cx="6" cy="18" r="2.5" /><circle cx="16" cy="16" r="2.5" /><path d="M8.5 18V6.5L18.5 4v11.5" /></svg>;
    case 'ring':
      return <svg {...common}><circle cx="12" cy="14" r="6" /><path d="M12 8L9 3h6l-3 5z" /></svg>;
    case 'calendar':
      return <svg {...common}><rect x="3.5" y="5" width="17" height="16" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" /></svg>;
    case 'lock':
      return <svg {...common}><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7.5a4 4 0 018 0V11" /></svg>;
    case 'lightbulb':
      return <svg {...common}><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 00-3.5 10.9c.5.4.8 1 .8 1.6v.5h5.4v-.5c0-.6.3-1.2.8-1.6A6 6 0 0012 3z" /></svg>;
    case 'game':
      return <svg {...common}><rect x="2.5" y="8" width="19" height="9" rx="4" /><path d="M7 10.5v4M5 12.5h4" /><circle cx="15.5" cy="11" r="0.8" fill="currentColor" /><circle cx="18" cy="13.5" r="0.8" fill="currentColor" /></svg>;
    case 'home':
      return <svg {...common}><path d="M4 11l8-7 8 7" /><path d="M6 9.5V20h12V9.5" /><path d="M10 20v-6h4v6" /></svg>;
    case 'sparkle':
      return <svg {...common}><path d="M12 3l1.8 5.6L19 10l-5.2 1.6L12 17l-1.8-5.4L5 10l5.2-1.4L12 3z" /></svg>;
    case 'poll':
      return <svg {...common}><path d="M5 20V10M12 20V4M19 20v-7" /></svg>;
    case 'settings':
      return <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M12 3v2M12 19v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M3 12h2M19 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" /></svg>;
    default:
      return null;
  }
};

// ---------- Confetti burst ----------
function useConfetti() {
  const [particles, setParticles] = useState([]);
  const burst = (x, y) => {
    const colors = ['#C97B84', '#C9A15C', '#4A2B3D', '#E8AFC0'];
    const batch = Array.from({ length: 14 }).map((_, i) => ({
      id: `${Date.now()}-${i}-${Math.random()}`,
      x, y,
      dx: (Math.random() - 0.5) * 160,
      dy: -Math.random() * 140 - 40,
      rotate: Math.random() * 360,
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
    setParticles((p) => [...p, ...batch]);
    setTimeout(() => {
      const ids = new Set(batch.map((b) => b.id));
      setParticles((p) => p.filter((particle) => !ids.has(particle.id)));
    }, 900);
  };
  const layer = (
    <div className="confetti-layer">
      {particles.map((p) => (
        <span key={p.id} className="confetti-piece" style={{ left: p.x, top: p.y, background: p.color, '--dx': `${p.dx}px`, '--dy': `${p.dy}px`, '--rotate': `${p.rotate}deg` }} />
      ))}
    </div>
  );
  return { burst, layer };
}

// ---------- Password gate ----------
function Gate({ title, subtitle, password, storageKey, onUnlock, icon = 'ring', variant = 'fullscreen' }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  const submit = () => {
    if (value === password) {
      localStorage.setItem(storageKey, 'true');
      onUnlock();
    } else {
      setError(true);
    }
  };

  const card = (
    <div className={variant === 'fullscreen' ? 'gate-card' : 'gate-card gate-card-inline'}>
      <Icon name={icon} size={32} />
      <h1>{title}</h1>
      <p>{subtitle}</p>
      <input
        type="password"
        value={value}
        onChange={(e) => { setValue(e.target.value); setError(false); }}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
        placeholder="Password"
        autoFocus
      />
      {error && <div className="gate-error">That's not it — try again.</div>}
      <button onClick={submit}>Unlock</button>
    </div>
  );

  if (variant === 'fullscreen') return <div className="gate">{card}</div>;
  return <div className="gate-inline-wrap">{card}</div>;
}

// ---------- Voting (shared by suggestions) ----------
async function castVote(suggestionId, direction) {
  const voteKey = `bp-vote-${suggestionId}`;
  const current = localStorage.getItem(voteKey);
  const updates = {};
  if (current === direction) {
    updates[direction === 'up' ? 'upvotes' : 'downvotes'] = increment(-1);
    localStorage.removeItem(voteKey);
  } else if (current) {
    updates[current === 'up' ? 'upvotes' : 'downvotes'] = increment(-1);
    updates[direction === 'up' ? 'upvotes' : 'downvotes'] = increment(1);
    localStorage.setItem(voteKey, direction);
  } else {
    updates[direction === 'up' ? 'upvotes' : 'downvotes'] = increment(1);
    localStorage.setItem(voteKey, direction);
  }
  await updateDoc(doc(db, 'suggestions', suggestionId), updates);
}

function VoteButtons({ suggestion }) {
  const voteKey = `bp-vote-${suggestion.id}`;
  const [myVote, setMyVote] = useState(() => localStorage.getItem(voteKey));
  const vote = async (direction) => {
    setMyVote((prev) => (prev === direction ? null : direction));
    try { await castVote(suggestion.id, direction); } catch (err) { console.error('Vote failed:', err); }
  };
  return (
    <div className="vote-buttons">
      <button type="button" className={`vote-btn ${myVote === 'up' ? 'voted-up' : ''}`} onClick={() => vote('up')}>👍 {suggestion.upvotes || 0}</button>
      <button type="button" className={`vote-btn ${myVote === 'down' ? 'voted-down' : ''}`} onClick={() => vote('down')}>👎 {suggestion.downvotes || 0}</button>
    </div>
  );
}

// ---------- Comments (reused by suggestions + icebreakers) ----------
function Comments({ parentCollection, parentId }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [name, setName] = useState(getStoredName());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const q = query(collection(db, `${parentCollection}/${parentId}/comments`), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, (snap) => setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [parentCollection, parentId]);

  const submit = async () => {
    if (!text.trim() || !name.trim()) return;
    setStoredName(name.trim());
    await addDoc(collection(db, `${parentCollection}/${parentId}/comments`), {
      text: text.trim(), submittedBy: name.trim(), createdAt: serverTimestamp(),
    });
    setText('');
  };

  return (
    <div className="comments-block">
      <button type="button" className="comments-toggle" onClick={() => setOpen(!open)}>
        {comments.length} comment{comments.length !== 1 ? 's' : ''} {open ? '▲' : '▼'}
      </button>
      {open && (
        <div className="comments-open">
          {comments.map((c) => (
            <div key={c.id} className="comment-row">
              <span className="comment-text">{c.text}</span>
              <span className="comment-from">— {c.submittedBy}</span>
            </div>
          ))}
          <div className="comment-form">
            <input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
            <input placeholder="Add a comment" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} />
            <button type="button" onClick={submit}>Post</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Suggestions tab ----------
function fetchLinkPreview(url) {
  return fetch(`https://api.microlink.io/?url=${encodeURIComponent(url)}`)
    .then((res) => res.json())
    .then((json) => (json.status === 'success'
      ? { previewTitle: json.data.title || '', previewImage: (json.data.image && json.data.image.url) || '' }
      : { previewTitle: '', previewImage: '' }))
    .catch(() => ({ previewTitle: '', previewImage: '' }));
}

function Suggestions({ suggestions, burstConfetti }) {
  const [category, setCategory] = useState('games');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [name, setName] = useState(getStoredName());
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const isAirbnb = category === 'airbnb';

  const handleSubmit = async (e) => {
    if (!name.trim() || submitting) return;
    if (isAirbnb && !url.trim()) return;
    if (!isAirbnb && !text.trim()) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setSubmitting(true);
    setStoredName(name.trim());
    try {
      let preview = { previewTitle: '', previewImage: '' };
      if (isAirbnb && url.trim()) preview = await fetchLinkPreview(url.trim());
      await addDoc(collection(db, 'suggestions'), {
        category, text: text.trim(), url: isAirbnb ? url.trim() : '',
        previewTitle: preview.previewTitle, previewImage: preview.previewImage,
        submittedBy: name.trim(), upvotes: 0, downvotes: 0, createdAt: serverTimestamp(),
      });
      burstConfetti(rect.left + rect.width / 2, rect.top);
      setText(''); setUrl('');
      setJustSubmitted(true);
      setTimeout(() => setJustSubmitted(false), 1800);
    } catch (err) {
      console.error('Failed to submit suggestion:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const grouped = SUGGESTION_CATEGORIES.map((cat) => ({ ...cat, items: suggestions.filter((s) => s.category === cat.id) }));

  return (
    <div className="tab-content">
      <p className="tab-intro">Got an idea for a game, a place to stay, or something fun to do? Drop it below — vote 👍👎 and comment to help pick favorites.</p>
      <div className="suggest-form">
        <input className="suggest-name-input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
        <div className="category-picker">
          {SUGGESTION_CATEGORIES.map((cat) => (
            <button key={cat.id} type="button" className={category === cat.id ? 'active' : ''} onClick={() => setCategory(cat.id)}>
              <Icon name={cat.icon} size={14} /> {cat.label}
            </button>
          ))}
        </div>
        {isAirbnb ? (
          <>
            <div className="airbnb-open-row">
              <a className="open-airbnb-btn" href="https://www.airbnb.com/s" target="_blank" rel="noreferrer">
                <Icon name="home" size={14} /> Open Airbnb in a new tab
              </a>
              <span className="airbnb-open-hint">search there, then paste the listing link below</span>
            </div>
            <input className="suggest-name-input" placeholder="Paste the Airbnb listing link here" value={url} onChange={(e) => setUrl(e.target.value)} />
            <textarea placeholder="Why you like it (optional)" value={text} onChange={(e) => setText(e.target.value)} rows={2} />
          </>
        ) : (
          <textarea placeholder="Share your idea..." value={text} onChange={(e) => setText(e.target.value)} rows={3} />
        )}
        <button className="submit-btn" onClick={handleSubmit} disabled={submitting || !name.trim() || (isAirbnb ? !url.trim() : !text.trim())}>
          {justSubmitted ? 'Added! ✨' : submitting ? 'Adding...' : 'Submit idea'}
        </button>
      </div>

      {grouped.map((cat) => cat.items.length > 0 && (
        <div key={cat.id} className="suggestion-group">
          <h3 className="section-heading"><Icon name={cat.icon} /> {cat.label}</h3>
          <div className="suggestion-list">
            {cat.items.map((item) => (
              <div key={item.id} className="suggestion-card">
                {item.previewImage && <img className="suggestion-preview-img" src={item.previewImage} alt="" />}
                {item.previewTitle && <div className="suggestion-preview-title">{item.previewTitle}</div>}
                {item.text && <div className="suggestion-text">{item.text}</div>}
                {item.url && <a className="suggestion-link" href={item.url} target="_blank" rel="noreferrer">View listing ↗</a>}
                <div className="suggestion-footer">
                  <div className="suggestion-from">— {item.submittedBy}</div>
                  <VoteButtons suggestion={item} />
                </div>
                <Comments parentCollection="suggestions" parentId={item.id} />
              </div>
            ))}
          </div>
        </div>
      ))}
      {suggestions.length === 0 && <div className="empty-state">No ideas yet — be the first to add one!</div>}
    </div>
  );
}

// ---------- Icebreakers tab ----------
function Icebreakers({ posts }) {
  const [name, setName] = useState(getStoredName());
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name.trim() || !text.trim() || submitting) return;
    setSubmitting(true);
    setStoredName(name.trim());
    try {
      await addDoc(collection(db, 'icebreakers'), { name: name.trim(), text: text.trim(), createdAt: serverTimestamp() });
      setText('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tab-content">
      <p className="tab-intro">Share something fun about yourself so everyone gets to know each other before the big weekend.</p>
      <div className="suggest-form">
        <input className="suggest-name-input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
        <textarea placeholder="Tell us something fun — favorite hobby, a random fact, how you know the bride..." value={text} onChange={(e) => setText(e.target.value)} rows={3} />
        <button className="submit-btn" onClick={submit} disabled={submitting || !name.trim() || !text.trim()}>{submitting ? 'Posting...' : 'Post'}</button>
      </div>
      <div className="suggestion-list">
        {posts.map((p) => (
          <div key={p.id} className="suggestion-card">
            <div className="suggestion-text">{p.text}</div>
            <div className="suggestion-footer"><div className="suggestion-from">— {p.name}</div></div>
            <Comments parentCollection="icebreakers" parentId={p.id} />
          </div>
        ))}
      </div>
      {posts.length === 0 && <div className="empty-state">No posts yet — be the first to introduce yourself!</div>}
    </div>
  );
}

// ---------- Directory tab (with self-edit) ----------
function DirectorySelfEdit({ members }) {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [verified, setVerified] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('');

  const selectedMember = members.find((m) => m.id === selectedId);

  const verify = () => {
    if (selectedMember && confirmEmail.trim().toLowerCase() === (selectedMember.email || '').trim().toLowerCase()) {
      setVerified(true);
      setName(selectedMember.name || '');
      setPhone(formatPhoneNumber(selectedMember.phone || ''));
      setStatus('');
    } else {
      setStatus("That email doesn't match what we have on file.");
    }
  };

  const save = async () => {
    if (!selectedMember) return;
    try {
      await updateDoc(doc(db, 'bridalParty', selectedMember.id), { name: name.trim(), phone: phone.trim() });
      setStatus('Updated! ✨');
      setTimeout(() => { setOpen(false); setVerified(false); setStatus(''); setSelectedId(''); setConfirmEmail(''); }, 1500);
    } catch (err) {
      setStatus('Something went wrong — try again.');
    }
  };

  if (!open) {
    return <button type="button" className="self-edit-toggle" onClick={() => setOpen(true)}>This info isn't right? Update it</button>;
  }

  return (
    <div className="self-edit-form">
      {!verified ? (
        <>
          <select value={selectedId} onChange={(e) => { setSelectedId(e.target.value); setStatus(''); }}>
            <option value="">Select your name</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <input placeholder="Confirm your email on file" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} />
          <button type="button" className="submit-btn" onClick={verify} disabled={!selectedId || !confirmEmail.trim()}>Verify</button>
        </>
      ) : (
        <>
          <input placeholder="Name / nickname" value={name} onChange={(e) => setName(e.target.value)} />
          <input type="tel" inputMode="tel" placeholder="(000) 000-0000" value={phone} onChange={(e) => setPhone(formatPhoneNumber(e.target.value))} />
          <button type="button" className="submit-btn" onClick={save}>Save changes</button>
        </>
      )}
      {status && <div className="self-edit-status">{status}</div>}
      <button type="button" className="self-edit-cancel" onClick={() => { setOpen(false); setVerified(false); setStatus(''); }}>Cancel</button>
    </div>
  );
}

function Directory({ members }) {
  if (!members.length) return <div className="empty-state">No contacts yet.</div>;
  return (
    <div className="tab-content">
      <p className="tab-intro">Everyone's number, so you can coordinate directly.</p>
      <div className="directory-grid">
        {members.map((m) => (
          <div key={m.id} className="directory-card">
            <div className="directory-name">{m.name || 'Unnamed'}</div>
            {m.phone && <a className="directory-phone" href={`tel:${m.phone}`}>{formatPhoneNumber(m.phone) || m.phone}</a>}
          </div>
        ))}
      </div>
      <DirectorySelfEdit members={members} />
    </div>
  );
}

// ---------- Schedule tab (party-facing) ----------
function Schedule({ events, scheduleEnabled }) {
  if (!scheduleEnabled) {
    return <div className="empty-state">🚧 The schedule is still being finalized — check back soon!</div>;
  }
  if (!events.length) return <div className="empty-state">No events added yet.</div>;
  return (
    <div className="tab-content">
      <div className="event-list">
        {events.map((ev) => (
          <div key={ev.id} className="event-card">
            <div className="event-card-date">{formatEventDate(ev.date)}</div>
            <div className="event-card-title">{ev.title}</div>
            {ev.location && <div className="event-card-meta"><Icon name="home" size={14} /> {ev.location}</div>}
            {ev.startTime && <div className="event-card-meta"><Icon name="calendar" size={14} /> {formatEventTime(ev.startTime)}{ev.endTime ? ` – ${formatEventTime(ev.endTime)}` : ''}</div>}
            {ev.notes && <div className="event-card-notes">{ev.notes}</div>}
            <button type="button" className="calendar-btn" onClick={() => downloadICS(ev)}>
              <Icon name="calendar" size={14} /> Add to calendar
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Polls tab (party-facing) ----------
function PollCard({ poll }) {
  const voteKey = `bp-poll-vote-${poll.id}`;
  const [myVote, setMyVote] = useState(() => localStorage.getItem(voteKey));
  const votes = poll.votes || {};
  const total = poll.options.reduce((sum, _, i) => sum + (votes[i] || 0), 0);

  const vote = async (idx) => {
    const updates = {};
    if (myVote === String(idx)) {
      updates[`votes.${idx}`] = increment(-1);
      localStorage.removeItem(voteKey);
      setMyVote(null);
    } else {
      if (myVote !== null) updates[`votes.${myVote}`] = increment(-1);
      updates[`votes.${idx}`] = increment(1);
      localStorage.setItem(voteKey, String(idx));
      setMyVote(String(idx));
    }
    await updateDoc(doc(db, 'polls', poll.id), updates);
  };

  return (
    <div className="suggestion-card">
      <div className="suggestion-preview-title">{poll.question}</div>
      <div className="poll-options">
        {poll.options.map((opt, i) => {
          const count = votes[i] || 0;
          const pct = total ? Math.round((count / total) * 100) : 0;
          const selected = myVote === String(i);
          return (
            <button key={i} type="button" className={`poll-option ${selected ? 'selected' : ''}`} onClick={() => vote(i)}>
              <span className="poll-option-fill" style={{ width: `${pct}%` }} />
              <span className="poll-option-label">{opt}</span>
              <span className="poll-option-count">{count} ({pct}%)</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Polls({ polls, pollsEnabled }) {
  if (!pollsEnabled) return <div className="empty-state">🚧 Polls aren't open yet — check back soon!</div>;
  const active = polls.filter((p) => p.active !== false);
  if (!active.length) return <div className="empty-state">No polls yet.</div>;
  return (
    <div className="tab-content">
      <div className="suggestion-list">{active.map((p) => <PollCard key={p.id} poll={p} />)}</div>
    </div>
  );
}

// ---------- Overview / Members / Sizes / Playlist (admin) ----------
function Overview({ members }) {
  const attendanceCounts = useMemo(() => {
    const counts = {};
    EVENT_LIST.forEach((e) => (counts[e] = 0));
    members.forEach((m) => (m.eventsAttending || []).forEach((e) => { if (counts[e] !== undefined) counts[e] += 1; }));
    return counts;
  }, [members]);

  const involvementCounts = useMemo(() => {
    const counts = {};
    members.forEach((m) => { const lvl = m.involvementLevel || 'Unspecified'; counts[lvl] = (counts[lvl] || 0) + 1; });
    return counts;
  }, [members]);

  return (
    <div className="tab-content">
      <div className="stat-row">
        <div className="stat-card">
          <div className="stat-number">{members.length}</div>
          <div className="stat-label">bridal party members responded</div>
        </div>
      </div>
      <h3 className="section-heading"><Icon name="calendar" /> Who's coming to what</h3>
      <div className="event-bars">
        {EVENT_LIST.map((event) => {
          const count = attendanceCounts[event];
          const pct = members.length ? (count / members.length) * 100 : 0;
          return (
            <div key={event} className="event-bar-row">
              <div className="event-bar-label">{event}</div>
              <div className="event-bar-track"><div className="event-bar-fill" style={{ width: `${pct}%` }} /></div>
              <div className="event-bar-count">{count}/{members.length}</div>
            </div>
          );
        })}
      </div>
      <h3 className="section-heading">Involvement preferences</h3>
      <div className="chip-row">
        {Object.entries(involvementCounts).map(([level, count]) => (
          <div key={level} className="chip"><span className="chip-count">{count}</span> {level}</div>
        ))}
      </div>
    </div>
  );
}

function Members({ members }) {
  const [expanded, setExpanded] = useState(null);
  if (!members.length) return <div className="empty-state">No responses yet.</div>;
  return (
    <div className="tab-content">
      <div className="member-grid">
        {members.map((m) => (
          <div key={m.id} className={`member-card ${expanded === m.id ? 'expanded' : ''}`} onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
            <div className="member-tag-hole" />
            <div className="member-name">{m.name || 'Unnamed'}</div>
            {m.weddingDayRole && <div className="member-role">{m.weddingDayRole}</div>}
            <div className="member-meta">{m.email}</div>
            <div className="member-meta">{formatPhoneNumber(m.phone) || m.phone}</div>
            {expanded === m.id && (
              <div className="member-details">
                <div className="detail-row"><span>Address</span><span>{m.mailingAddress}</span></div>
                <div className="detail-row"><span>Emergency contact</span><span>{m.emergencyContact}</span></div>
                <div className="detail-row"><span>Unavailable dates</span><span>{m.unavailableDates || 'None listed'}</span></div>
                <div className="detail-row"><span>Involvement</span><span>{m.involvementLevel}</span></div>
                <div className="detail-row"><span>Wedding day role</span><span>{m.weddingDayRole}</span></div>
                <div className="detail-row"><span>Attending</span><span>{(m.eventsAttending || []).join(', ') || 'None selected'}</span></div>
                <div className="detail-row"><span>Coffee order</span><span>{m.coffeeOrder}</span></div>
                <div className="detail-row"><span>Favorite snacks</span><span>{m.favSnacks}</span></div>
                <div className="detail-row"><span>Non-alcoholic drink</span><span>{m.favNonAlcoholicDrink}</span></div>
                <div className="detail-row"><span>Alcoholic drink</span><span>{m.favAlcoholicDrink}</span></div>
                <div className="detail-row"><span>Dietary restrictions</span><span>{m.dietaryRestrictions || 'None'}</span></div>
                <div className="detail-row"><span>Disliked foods</span><span>{m.dislikedFoods || 'None'}</span></div>
                <div className="detail-row"><span>Comfortable sharing a room</span><span>{m.comfortableSharingRoom}</span></div>
                <div className="detail-row"><span>Comfortable budget range</span><span>{m.comfortableBudgetRange}</span></div>
                <div className="detail-row"><span>Max wedding spend</span><span>{m.maxWeddingSpend}</span></div>
                <div className="detail-row"><span>Comfy giving a speech</span><span>{m.comfyGivingSpeech}</span></div>
                <div className="detail-row"><span>Wants pro hair/makeup</span><span>{m.interestedInHairMakeup}</span></div>
                <div className="detail-row"><span>Songs</span><span>{m.favoriteSongs}</span></div>
                <div className="detail-row"><span>Nervous about</span><span>{m.nervousAbout || 'Nothing mentioned'}</span></div>
                <div className="detail-row"><span>Suggestions</span><span>{m.suggestions || 'None'}</span></div>
              </div>
            )}
            <div className="member-tap-hint">{expanded === m.id ? 'tap to collapse' : 'tap for details'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Sizes({ members }) {
  if (!members.length) return <div className="empty-state">No sizes to show yet.</div>;
  return (
    <div className="tab-content">
      <table className="sizes-table">
        <thead><tr><th>Name</th><th>Robe</th><th>Pajama Top</th><th>Pajama Bottom</th><th>Shorts / Pants</th><th>Slipper</th></tr></thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id}><td>{m.name}</td><td>{m.robeSize}</td><td>{m.pajamaTopSize}</td><td>{m.pajamaBottomSize}</td><td>{m.shortsOrPants}</td><td>{m.slipperSize}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Playlist({ members }) {
  const allSongs = useMemo(() => {
    const list = [];
    members.forEach((m) => {
      if (!m.favoriteSongs) return;
      m.favoriteSongs.split(/,|\n/).map((s) => s.trim()).filter(Boolean).forEach((song) => list.push({ song, from: m.name }));
    });
    return list;
  }, [members]);
  if (!allSongs.length) return <div className="empty-state">No songs collected yet.</div>;
  return (
    <div className="tab-content">
      <p className="playlist-intro">Every song the bridal party said gets them dancing.</p>
      <div className="playlist-list">
        {allSongs.map((item, i) => (
          <div key={i} className="playlist-row"><Icon name="playlist" size={16} /><span className="playlist-song">{item.song}</span><span className="playlist-from">— {item.from}</span></div>
        ))}
      </div>
    </div>
  );
}

// ---------- Events admin ----------
function EventsAdmin({ events }) {
  const emptyForm = { title: '', date: '', startTime: '', endTime: '', location: '', notes: '' };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };

  const save = async () => {
    if (!form.title.trim() || !form.date) return;
    if (editingId) {
      await updateDoc(doc(db, 'events', editingId), { ...form });
    } else {
      await addDoc(collection(db, 'events'), { ...form, createdAt: serverTimestamp() });
    }
    resetForm();
  };

  const edit = (ev) => {
    setForm({ title: ev.title || '', date: ev.date || '', startTime: ev.startTime || '', endTime: ev.endTime || '', location: ev.location || '', notes: ev.notes || '' });
    setEditingId(ev.id);
  };

  const remove = async (id) => { await deleteDoc(doc(db, 'events', id)); };

  return (
    <div className="tab-content">
      <div className="suggest-form">
        <input className="suggest-name-input" placeholder="Event title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <div className="event-form-row">
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
          <input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
        </div>
        <input className="suggest-name-input" placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        <textarea placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
        <div className="event-form-actions">
          <button type="button" className="submit-btn" onClick={save}>{editingId ? 'Save changes' : 'Add event'}</button>
          {editingId && <button type="button" className="vote-btn" onClick={resetForm}>Cancel</button>}
        </div>
      </div>
      <div className="event-list">
        {events.map((ev) => (
          <div key={ev.id} className="event-card">
            <div className="event-card-date">{formatEventDate(ev.date)}</div>
            <div className="event-card-title">{ev.title}</div>
            <div className="event-form-actions">
              <button type="button" className="vote-btn" onClick={() => edit(ev)}>Edit</button>
              <button type="button" className="vote-btn" onClick={() => remove(ev.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Polls admin ----------
function PollsAdmin({ polls }) {
  const [question, setQuestion] = useState('');
  const [optionsText, setOptionsText] = useState('');

  const create = async () => {
    const options = optionsText.split('\n').map((s) => s.trim()).filter(Boolean);
    if (!question.trim() || options.length < 2) return;
    await addDoc(collection(db, 'polls'), { question: question.trim(), options, votes: {}, active: true, createdAt: serverTimestamp() });
    setQuestion(''); setOptionsText('');
  };

  const toggleActive = async (poll) => {
    const current = poll.active !== false;
    await updateDoc(doc(db, 'polls', poll.id), { active: !current });
  };

  const remove = async (id) => { await deleteDoc(doc(db, 'polls', id)); };

  return (
    <div className="tab-content">
      <div className="suggest-form">
        <input className="suggest-name-input" placeholder="Poll question" value={question} onChange={(e) => setQuestion(e.target.value)} />
        <textarea placeholder="One option per line (at least 2)" value={optionsText} onChange={(e) => setOptionsText(e.target.value)} rows={4} />
        <button type="button" className="submit-btn" onClick={create}>Create poll</button>
      </div>
      <div className="suggestion-list">
        {polls.map((p) => (
          <div key={p.id} className="suggestion-card">
            <div className="suggestion-preview-title">{p.question}{p.active === false ? ' (hidden from party)' : ''}</div>
            {p.options.map((opt, i) => (
              <div key={i} className="detail-row"><span>{opt}</span><span>{(p.votes && p.votes[i]) || 0} votes</span></div>
            ))}
            <div className="event-form-actions">
              <button type="button" className="vote-btn" onClick={() => toggleActive(p)}>{p.active === false ? 'Show to party' : 'Hide from party'}</button>
              <button type="button" className="vote-btn" onClick={() => remove(p.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Settings admin (feature toggles) ----------
function SettingsAdmin({ settings }) {
  const toggle = async (key) => {
    await setDoc(doc(db, 'settings', 'features'), { [key]: !settings[key] }, { merge: true });
  };
  return (
    <div className="tab-content">
      <div className="toggle-row">
        <div>
          <div className="toggle-label">Schedule tab</div>
          <div className="toggle-sub">When off, the party sees a work-in-progress message instead of event details.</div>
        </div>
        <button type="button" className={`toggle-switch ${settings.scheduleEnabled ? 'on' : ''}`} onClick={() => toggle('scheduleEnabled')}><span className="toggle-knob" /></button>
      </div>
      <div className="toggle-row">
        <div>
          <div className="toggle-label">Polls tab</div>
          <div className="toggle-sub">When off, the party sees a work-in-progress message instead of polls.</div>
        </div>
        <button type="button" className={`toggle-switch ${settings.pollsEnabled ? 'on' : ''}`} onClick={() => toggle('pollsEnabled')}><span className="toggle-knob" /></button>
      </div>
    </div>
  );
}

// ---------- Admin section (nested gate + subtabs) ----------
function AdminSection({ members, events, polls, settings, adminUnlocked, onAdminUnlock }) {
  const [subtab, setSubtab] = useState('overview');

  if (!adminUnlocked) {
    return (
      <Gate
        title="Zoë's Eyes Only"
        subtitle="This has everyone's full responses, plus the event/poll admin tools. Enter the admin password to view."
        password={ADMIN_PASSWORD}
        storageKey="bp-admin-unlocked"
        onUnlock={onAdminUnlock}
        icon="lock"
        variant="inline"
      />
    );
  }

  const subtabs = [
    { id: 'overview', label: 'Overview', icon: 'overview' },
    { id: 'members', label: 'Bridal Party', icon: 'members' },
    { id: 'sizes', label: 'Sizes', icon: 'sizes' },
    { id: 'playlist', label: 'Playlist', icon: 'playlist' },
    { id: 'events', label: 'Events', icon: 'calendar' },
    { id: 'polls', label: 'Polls', icon: 'poll' },
    { id: 'settings', label: 'Settings', icon: 'settings' },
  ];

  return (
    <div>
      <nav className="subtab-nav">
        {subtabs.map((t) => (
          <button key={t.id} className={subtab === t.id ? 'active' : ''} onClick={() => setSubtab(t.id)}>
            <Icon name={t.icon} size={14} /> {t.label}
          </button>
        ))}
      </nav>
      {subtab === 'overview' && <Overview members={members} />}
      {subtab === 'members' && <Members members={members} />}
      {subtab === 'sizes' && <Sizes members={members} />}
      {subtab === 'playlist' && <Playlist members={members} />}
      {subtab === 'events' && <EventsAdmin events={events} />}
      {subtab === 'polls' && <PollsAdmin polls={polls} />}
      {subtab === 'settings' && <SettingsAdmin settings={settings} />}
    </div>
  );
}

// ---------- Main App ----------
export default function App() {
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem('bp-party-unlocked') === 'true');
  const [adminUnlocked, setAdminUnlocked] = useState(() => localStorage.getItem('bp-admin-unlocked') === 'true');
  const [tab, setTab] = useState('suggestions');
  const [members, setMembers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [icebreakers, setIcebreakers] = useState([]);
  const [events, setEvents] = useState([]);
  const [polls, setPolls] = useState([]);
  const [settings, setSettings] = useState({ pollsEnabled: false, scheduleEnabled: false });
  const [loading, setLoading] = useState(true);
  const { burst, layer } = useConfetti();

  useEffect(() => {
    if (!unlocked) return;
    const unsubs = [
      onSnapshot(collection(db, 'bridalParty'), (snap) => { setMembers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); }),
      onSnapshot(query(collection(db, 'suggestions'), orderBy('createdAt', 'desc')), (snap) => setSuggestions(snap.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(query(collection(db, 'icebreakers'), orderBy('createdAt', 'desc')), (snap) => setIcebreakers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(query(collection(db, 'events'), orderBy('date', 'asc')), (snap) => setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(query(collection(db, 'polls'), orderBy('createdAt', 'desc')), (snap) => setPolls(snap.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(doc(db, 'settings', 'features'), (snap) => setSettings(snap.exists() ? snap.data() : { pollsEnabled: false, scheduleEnabled: false })),
    ];
    return () => unsubs.forEach((u) => u());
  }, [unlocked]);

  if (!unlocked) {
    return (
      <>
        <style>{CSS}</style>
        <Gate title="Bridal Party Planning" subtitle="Enter the password to join in." password={PARTY_PASSWORD} storageKey="bp-party-unlocked" onUnlock={() => setUnlocked(true)} icon="ring" />
      </>
    );
  }

  const tabs = [
    { id: 'suggestions', label: 'Suggest Ideas', icon: 'lightbulb' },
    { id: 'icebreakers', label: 'Icebreakers', icon: 'sparkle' },
    { id: 'directory', label: 'Contact List', icon: 'members' },
    { id: 'schedule', label: 'Schedule', icon: 'calendar' },
    { id: 'polls', label: 'Polls', icon: 'poll' },
    { id: 'admin', label: 'Full Details', icon: 'lock' },
  ];

  return (
    <div className="app">
      <style>{CSS}</style>
      {layer}
      <header className="app-header">
        <Icon name="ring" size={26} />
        <div><h1>Bridal Party Planning</h1><p>Everything for the crew, all in one place.</p></div>
      </header>
      <nav className="tab-nav">
        {tabs.map((t) => (
          <button key={t.id} className={tab === t.id ? 'active' : ''} onClick={() => setTab(t.id)}>
            <Icon name={t.icon} size={16} /> {t.label}
          </button>
        ))}
      </nav>
      <main>
        {loading ? <div className="empty-state">Loading...</div> : (
          <>
            {tab === 'suggestions' && <Suggestions suggestions={suggestions} burstConfetti={burst} />}
            {tab === 'icebreakers' && <Icebreakers posts={icebreakers} />}
            {tab === 'directory' && <Directory members={members} />}
            {tab === 'schedule' && <Schedule events={events} scheduleEnabled={!!settings.scheduleEnabled} />}
            {tab === 'polls' && <Polls polls={polls} pollsEnabled={!!settings.pollsEnabled} />}
            {tab === 'admin' && (
              <AdminSection
                members={members}
                events={events}
                polls={polls}
                settings={settings}
                adminUnlocked={adminUnlocked}
                onAdminUnlock={() => setAdminUnlocked(true)}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}

// ---------- Styles ----------
const CSS = `
  * { box-sizing: border-box; }
  body { margin: 0; }
  .app, .gate {
    font-family: 'Georgia', 'Iowan Old Style', serif;
    background: #FBF3EE;
    color: #4A2B3D;
    min-height: 100vh;
  }
  .gate { display: flex; align-items: center; justify-content: center; }
  .gate-inline-wrap { display: flex; justify-content: center; padding: 60px 20px; }
  .gate-card {
    background: #fff; border: 1px solid #E8D4CE; border-radius: 4px;
    padding: 40px 36px; text-align: center; max-width: 320px;
    box-shadow: 0 12px 32px rgba(74,43,61,0.08);
  }
  .gate-card-inline { box-shadow: none; border: 1px dashed #E8D4CE; }
  .gate-card h1 { font-size: 22px; margin: 12px 0 4px; }
  .gate-card p { color: #8A6572; font-size: 14px; margin-bottom: 20px; }
  .gate-card input {
    width: 100%; padding: 10px 12px; border: 1px solid #E8D4CE; border-radius: 3px;
    font-size: 14px; margin-bottom: 8px; font-family: inherit;
  }
  .gate-error { color: #B5495B; font-size: 12px; margin-bottom: 10px; }
  .gate-card button {
    width: 100%; padding: 10px; background: #C97B84; color: #fff; border: none;
    border-radius: 3px; font-size: 14px; cursor: pointer; letter-spacing: 0.03em;
    transition: transform 0.12s ease, background 0.15s ease;
  }
  .gate-card button:hover { background: #B5697C; }
  .gate-card button:active { transform: scale(0.96); }

  .app-header { display: flex; align-items: center; gap: 14px; padding: 32px 28px 20px; border-bottom: 1px solid #E8D4CE; }
  .app-header h1 { font-size: 24px; margin: 0; letter-spacing: 0.01em; }
  .app-header p { font-size: 13px; color: #8A6572; margin: 2px 0 0; font-style: italic; }

  .tab-nav, .subtab-nav { display: flex; gap: 4px; padding: 14px 28px 0; flex-wrap: wrap; border-bottom: 1px solid #E8D4CE; }
  .subtab-nav { padding: 0; margin-bottom: 20px; }
  .tab-nav button, .subtab-nav button {
    display: flex; align-items: center; gap: 6px; background: none; border: none; padding: 10px 16px;
    font-family: inherit; font-size: 13px; color: #8A6572; cursor: pointer;
    border-bottom: 2px solid transparent; letter-spacing: 0.02em;
    transition: color 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
  }
  .tab-nav button:active, .subtab-nav button:active { transform: scale(0.97); }
  .tab-nav button.active, .subtab-nav button.active { color: #4A2B3D; border-bottom-color: #C9A15C; font-weight: bold; }

  main { padding: 28px; max-width: 1000px; margin: 0 auto; }
  .empty-state { color: #8A6572; font-style: italic; padding: 40px 0; text-align: center; }
  .tab-intro { color: #8A6572; font-size: 13px; font-style: italic; margin-bottom: 20px; }

  button { transition: transform 0.12s ease; }
  button:active { transform: scale(0.96); }

  .stat-row { display: flex; gap: 16px; margin-bottom: 32px; }
  .stat-card { background: #fff; border: 1px solid #E8D4CE; border-radius: 4px; padding: 20px 28px; }
  .stat-number { font-size: 36px; color: #C97B84; font-weight: bold; }
  .stat-label { font-size: 12px; color: #8A6572; margin-top: 4px; }

  .section-heading { display: flex; align-items: center; gap: 8px; font-size: 15px; color: #4A2B3D; margin: 28px 0 14px; letter-spacing: 0.02em; }

  .event-bar-row { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
  .event-bar-label { width: 260px; font-size: 12px; color: #4A2B3D; flex-shrink: 0; }
  .event-bar-track { flex: 1; height: 8px; background: #E8D4CE; border-radius: 4px; overflow: hidden; }
  .event-bar-fill { height: 100%; background: #C9A15C; transition: width 0.4s ease; }
  .event-bar-count { font-size: 11px; color: #8A6572; width: 50px; text-align: right; }

  .chip-row { display: flex; gap: 10px; flex-wrap: wrap; }
  .chip { background: #fff; border: 1px solid #E8D4CE; border-radius: 20px; padding: 8px 16px; font-size: 12px; color: #4A2B3D; }
  .chip-count { color: #C97B84; font-weight: bold; }

  .member-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 18px; }
  .member-card {
    background: #fff; border: 1px solid #E8D4CE; border-radius: 4px; padding: 20px 18px 14px;
    position: relative; cursor: pointer; border-top: 3px dashed #E8D4CE;
    transition: box-shadow 0.15s ease, transform 0.15s ease;
  }
  .member-card:hover { box-shadow: 0 8px 20px rgba(74,43,61,0.08); transform: translateY(-3px); }
  .member-tag-hole { position: absolute; top: -8px; left: 50%; transform: translateX(-50%); width: 14px; height: 14px; border-radius: 50%; background: #FBF3EE; border: 1px solid #E8D4CE; }
  .member-name { font-size: 17px; font-weight: bold; margin-bottom: 4px; }
  .member-role { display: inline-block; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #C97B84; background: #F5E6DF; border-radius: 10px; padding: 2px 10px; margin-bottom: 8px; }
  .member-meta { font-size: 12px; color: #8A6572; }
  .member-details { margin-top: 14px; border-top: 1px solid #E8D4CE; padding-top: 12px; }
  .detail-row { display: flex; justify-content: space-between; gap: 10px; font-size: 12px; margin-bottom: 8px; }
  .detail-row span:first-child { color: #8A6572; flex-shrink: 0; }
  .detail-row span:last-child { text-align: right; }
  .member-tap-hint { font-size: 10px; color: #C9A15C; margin-top: 10px; letter-spacing: 0.03em; }

  .sizes-table { width: 100%; border-collapse: collapse; background: #fff; }
  .sizes-table th, .sizes-table td { border: 1px solid #E8D4CE; padding: 10px 14px; font-size: 13px; text-align: left; }
  .sizes-table th { background: #F5E6DF; color: #4A2B3D; font-weight: bold; }

  .playlist-intro { font-size: 13px; color: #8A6572; font-style: italic; margin-bottom: 20px; }
  .playlist-list { display: flex; flex-direction: column; gap: 10px; }
  .playlist-row { display: flex; align-items: center; gap: 8px; background: #fff; border: 1px solid #E8D4CE; border-radius: 4px; padding: 10px 14px; font-size: 13px; }
  .playlist-song { font-weight: bold; }
  .playlist-from { color: #8A6572; font-size: 12px; }

  .suggest-form { background: #fff; border: 1px solid #E8D4CE; border-radius: 4px; padding: 20px; margin-bottom: 30px; }
  .suggest-name-input { width: 100%; padding: 10px 12px; border: 1px solid #E8D4CE; border-radius: 3px; font-size: 13px; margin-bottom: 12px; font-family: inherit; }
  .category-picker { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
  .category-picker button { display: flex; align-items: center; gap: 6px; background: #FBF3EE; border: 1px solid #E8D4CE; border-radius: 20px; padding: 7px 14px; font-size: 12px; color: #8A6572; cursor: pointer; font-family: inherit; transition: all 0.15s ease; }
  .category-picker button.active { background: #C97B84; color: #fff; border-color: #C97B84; }

  .airbnb-open-row { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; }
  .open-airbnb-btn { display: flex; align-items: center; gap: 6px; background: #4A2B3D; color: #fff; text-decoration: none; border-radius: 20px; padding: 8px 16px; font-size: 12px; transition: background 0.15s ease, transform 0.12s ease; }
  .open-airbnb-btn:hover { background: #3A2130; }
  .open-airbnb-btn:active { transform: scale(0.96); }
  .airbnb-open-hint { font-size: 11px; color: #8A6572; font-style: italic; }

  .suggest-form textarea { width: 100%; padding: 10px 12px; border: 1px solid #E8D4CE; border-radius: 3px; font-size: 13px; font-family: inherit; resize: vertical; margin-bottom: 12px; }
  .submit-btn { background: #C9A15C; color: #fff; border: none; border-radius: 3px; padding: 10px 20px; font-size: 13px; cursor: pointer; font-family: inherit; letter-spacing: 0.02em; transition: background 0.15s ease, transform 0.12s ease; }
  .submit-btn:hover:not(:disabled) { background: #B58E4C; }
  .submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .suggestion-group { margin-bottom: 10px; }
  .suggestion-list { display: flex; flex-direction: column; gap: 10px; }
  .suggestion-card { background: #fff; border: 1px solid #E8D4CE; border-radius: 4px; padding: 14px 16px; font-size: 13px; animation: fade-in-up 0.3s ease; }
  .suggestion-preview-img { width: 100%; max-height: 180px; object-fit: cover; border-radius: 3px; margin-bottom: 10px; }
  .suggestion-preview-title { font-weight: bold; margin-bottom: 4px; }
  .suggestion-text { margin-bottom: 6px; }
  .suggestion-link { display: inline-block; color: #C97B84; font-size: 12px; text-decoration: none; margin-bottom: 8px; }
  .suggestion-link:hover { text-decoration: underline; }
  .suggestion-footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 8px; padding-top: 8px; border-top: 1px solid #F5E6DF; }
  .suggestion-from { color: #8A6572; font-size: 11px; }

  .vote-buttons { display: flex; gap: 6px; }
  .vote-btn { display: flex; align-items: center; gap: 4px; background: #FBF3EE; border: 1px solid #E8D4CE; border-radius: 14px; padding: 4px 10px; font-size: 12px; color: #4A2B3D; cursor: pointer; font-family: inherit; transition: all 0.15s ease; }
  .vote-btn:hover { border-color: #C9A15C; }
  .vote-btn.voted-up { background: #E8F5E9; border-color: #7CB88F; }
  .vote-btn.voted-down { background: #FCE8E8; border-color: #D98080; }

  .comments-block { margin-top: 10px; padding-top: 8px; border-top: 1px dashed #F5E6DF; }
  .comments-toggle { background: none; border: none; color: #8A6572; font-size: 11px; cursor: pointer; padding: 4px 0; font-family: inherit; }
  .comments-open { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
  .comment-row { display: flex; justify-content: space-between; gap: 8px; font-size: 12px; background: #FBF3EE; border-radius: 4px; padding: 6px 10px; }
  .comment-from { color: #8A6572; font-size: 10px; flex-shrink: 0; }
  .comment-form { display: flex; gap: 6px; margin-top: 6px; flex-wrap: wrap; }
  .comment-form input { flex: 1; min-width: 100px; padding: 6px 10px; border: 1px solid #E8D4CE; border-radius: 3px; font-size: 12px; font-family: inherit; }
  .comment-form button { background: #C97B84; color: #fff; border: none; border-radius: 3px; padding: 6px 12px; font-size: 12px; cursor: pointer; font-family: inherit; }

  .directory-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; margin-bottom: 20px; }
  .directory-card { background: #fff; border: 1px solid #E8D4CE; border-radius: 4px; padding: 16px 18px; transition: transform 0.15s ease, box-shadow 0.15s ease; }
  .directory-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(74,43,61,0.08); }
  .directory-name { font-weight: bold; font-size: 14px; margin-bottom: 4px; }
  .directory-phone { color: #C97B84; font-size: 13px; text-decoration: none; }
  .directory-phone:hover { text-decoration: underline; }

  .self-edit-toggle { background: none; border: none; color: #8A6572; font-size: 12px; text-decoration: underline; cursor: pointer; font-family: inherit; padding: 0; }
  .self-edit-form { background: #fff; border: 1px dashed #E8D4CE; border-radius: 4px; padding: 16px; display: flex; flex-direction: column; gap: 10px; max-width: 320px; }
  .self-edit-form select, .self-edit-form input { padding: 9px 12px; border: 1px solid #E8D4CE; border-radius: 3px; font-size: 13px; font-family: inherit; }
  .self-edit-status { font-size: 12px; color: #8A6572; }
  .self-edit-cancel { background: none; border: none; color: #8A6572; font-size: 11px; text-decoration: underline; cursor: pointer; font-family: inherit; padding: 0; align-self: flex-start; }

  .event-list { display: flex; flex-direction: column; gap: 12px; }
  .event-card { background: #fff; border: 1px solid #E8D4CE; border-radius: 4px; padding: 16px 18px; }
  .event-card-date { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: #C9A15C; margin-bottom: 4px; }
  .event-card-title { font-size: 16px; font-weight: bold; margin-bottom: 6px; }
  .event-card-meta { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #8A6572; margin-bottom: 4px; }
  .event-card-notes { font-size: 12px; color: #4A2B3D; margin-top: 6px; font-style: italic; }
  .calendar-btn { display: flex; align-items: center; gap: 6px; background: #4A2B3D; color: #fff; border: none; border-radius: 20px; padding: 8px 16px; font-size: 12px; cursor: pointer; font-family: inherit; margin-top: 10px; }
  .calendar-btn:hover { background: #3A2130; }
  .event-form-row { display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
  .event-form-row input { padding: 9px 12px; border: 1px solid #E8D4CE; border-radius: 3px; font-size: 13px; font-family: inherit; }
  .event-form-actions { display: flex; gap: 8px; margin-top: 8px; }

  .poll-options { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
  .poll-option { position: relative; text-align: left; background: #FBF3EE; border: 1px solid #E8D4CE; border-radius: 4px; padding: 10px 14px; font-size: 13px; cursor: pointer; font-family: inherit; overflow: hidden; display: flex; justify-content: space-between; }
  .poll-option-fill { position: absolute; left: 0; top: 0; bottom: 0; background: #F5E6DF; z-index: 0; transition: width 0.4s ease; }
  .poll-option-label, .poll-option-count { position: relative; z-index: 1; }
  .poll-option.selected { border-color: #C97B84; }
  .poll-option.selected .poll-option-fill { background: #F0D5D9; }

  .toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 20px; background: #fff; border: 1px solid #E8D4CE; border-radius: 4px; padding: 16px 18px; margin-bottom: 12px; }
  .toggle-label { font-weight: bold; font-size: 14px; }
  .toggle-sub { font-size: 12px; color: #8A6572; margin-top: 2px; }
  .toggle-switch { width: 44px; height: 24px; border-radius: 12px; background: #E8D4CE; border: none; position: relative; cursor: pointer; flex-shrink: 0; transition: background 0.2s ease; }
  .toggle-switch.on { background: #C9A15C; }
  .toggle-knob { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: transform 0.2s ease; display: block; }
  .toggle-switch.on .toggle-knob { transform: translateX(20px); }

  .confetti-layer { position: fixed; inset: 0; pointer-events: none; z-index: 999; }
  .confetti-piece { position: absolute; width: 8px; height: 8px; border-radius: 2px; animation: confetti-burst 0.9s ease-out forwards; }
  @keyframes confetti-burst { 0% { transform: translate(0, 0) rotate(0deg); opacity: 1; } 100% { transform: translate(var(--dx), var(--dy)) rotate(var(--rotate)); opacity: 0; } }
  @keyframes fade-in-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
`;
